import React from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  Card,
  DataTable,
  DataTableColumn,
  SegmentedControl,
  ToastProvider,
  useToast
} from '../../design-system';
import { voiceApi, VoiceAnalyticsDay } from './voiceClient';

interface Props { storeId: string; }

const RANGES: Array<{ value: '7d' | '14d' | '30d'; label: string; days: number }> = [
  { value: '7d', label: '7 days', days: 7 },
  { value: '14d', label: '14 days', days: 14 },
  { value: '30d', label: '30 days', days: 30 }
];

const isoDay = (d: Date) => d.toISOString().slice(0, 10);

const Inner: React.FC<Props> = ({ storeId }) => {
  const { getIdToken } = useAuth();
  const { show } = useToast();
  const [range, setRange] = React.useState<'7d' | '14d' | '30d'>('7d');
  const [days, setDays] = React.useState<VoiceAnalyticsDay[]>([]);
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    const cfg = RANGES.find((r) => r.value === range)!;
    const to = new Date();
    const from = new Date(Date.now() - cfg.days * 24 * 60 * 60 * 1000);
    setLoading(true);
    voiceApi.getAnalytics(getIdToken, storeId, isoDay(from), isoDay(to))
      .then((r) => setDays(r.days))
      .catch((err) => show({ tone: 'error', title: 'Failed to load analytics', description: err.message }))
      .finally(() => setLoading(false));
  }, [range, getIdToken, storeId, show]);

  const totals = React.useMemo(() => {
    return days.reduce((acc, d) => {
      acc.sessions += d.sessionCount;
      acc.cloud += d.cloudCount;
      acc.edge += d.edgeCount;
      acc.degraded += d.degradedCount;
      acc.items += d.itemsAddedTotal;
      acc.tools += d.toolCallTotal;
      acc.errors += d.errorTotal;
      return acc;
    }, { sessions: 0, cloud: 0, edge: 0, degraded: 0, items: 0, tools: 0, errors: 0 });
  }, [days]);

  const edgePct = totals.sessions ? Math.round((totals.edge / totals.sessions) * 100) : 0;
  const errorRate = totals.sessions ? ((totals.errors / totals.sessions) * 100).toFixed(1) : '0.0';

  const columns: DataTableColumn<VoiceAnalyticsDay>[] = [
    { key: 'date', header: 'Date', accessor: (r) => r.date, sortable: true },
    { key: 'sessions', header: 'Sessions', accessor: (r) => r.sessionCount, align: 'right', sortable: true, compare: (a, b) => a.sessionCount - b.sessionCount },
    { key: 'cloud', header: 'Cloud', accessor: (r) => r.cloudCount, align: 'right' },
    { key: 'edge', header: 'Edge', accessor: (r) => r.edgeCount, align: 'right' },
    { key: 'degraded', header: 'Degraded', accessor: (r) => r.degradedCount, align: 'right' },
    { key: 'avgLatency', header: 'Avg latency', accessor: (r) => r.avgLatencyMs ? `${r.avgLatencyMs} ms` : '—', align: 'right' },
    { key: 'items', header: 'Items added', accessor: (r) => r.itemsAddedTotal, align: 'right' },
    { key: 'errors', header: 'Errors', accessor: (r) => r.errorTotal, align: 'right' }
  ];

  return (
    <div data-theme="dark" style={{ padding: 'var(--bso-space-8) var(--bso-space-10)', background: 'var(--bso-bg-base)', color: 'var(--bso-text-primary)', fontFamily: 'var(--bso-font-sans)' }}>
      <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 'var(--bso-space-6)' }}>
        <div>
          <h1 style={{ margin: '0 0 var(--bso-space-2)' }}>Voice analytics</h1>
          <p style={{ color: 'var(--bso-text-secondary)', margin: 0 }}>Store {storeId}</p>
        </div>
        <SegmentedControl<'7d' | '14d' | '30d'>
          aria-label="Date range"
          value={range}
          onChange={setRange}
          options={RANGES.map((r) => ({ value: r.value, label: r.label }))}
        />
      </header>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 'var(--bso-space-4)', marginBottom: 'var(--bso-space-8)' }}>
        <Stat label="Sessions" value={totals.sessions.toLocaleString()} />
        <Stat label="Cloud / Edge / Degraded" value={`${totals.cloud} / ${totals.edge} / ${totals.degraded}`} />
        <Stat label="Edge fallback share" value={`${edgePct}%`} tone={edgePct > 30 ? 'warning' : undefined} />
        <Stat label="Items added via voice" value={totals.items.toLocaleString()} />
        <Stat label="Tool calls" value={totals.tools.toLocaleString()} />
        <Stat label="Error rate" value={`${errorRate}%`} tone={parseFloat(errorRate) > 5 ? 'error' : undefined} />
      </div>

      <Card>
        <Card.Header title="Daily breakdown" subtitle={loading ? 'Loading…' : `${days.length} day(s)`} />
        <Card.Body>
          <DataTable<VoiceAnalyticsDay>
            columns={columns}
            rows={days}
            getRowKey={(r) => r.date}
            zebra
            pageSize={14}
            emptyState={loading ? 'Loading…' : 'No voice sessions in this window.'}
          />
        </Card.Body>
      </Card>
    </div>
  );
};

const Stat: React.FC<{ label: string; value: string; tone?: 'warning' | 'error' }> = ({ label, value, tone }) => {
  const color = tone === 'error' ? 'var(--bso-danger)' : tone === 'warning' ? 'var(--bso-warning)' : 'var(--bso-text-primary)';
  return (
    <Card>
      <Card.Body>
        <div style={{ fontSize: 'var(--bso-text-xs)', color: 'var(--bso-text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>{label}</div>
        <div style={{ fontSize: 'var(--bso-text-2xl)', fontWeight: 'var(--bso-weight-semibold)', color, marginTop: 'var(--bso-space-2)' }}>{value}</div>
      </Card.Body>
    </Card>
  );
};

export const VoiceAnalyticsDashboard: React.FC<Props> = (props) => (
  <ToastProvider>
    <Inner {...props} />
  </ToastProvider>
);
